import { Suspense } from 'react';
import { useNavigate, useParams } from 'react-router';
import { SDKApplicationDetail } from '@gonfalon/billing/components';

import { Card, ErrorCallout } from '../../shared';
import { Box, Flex } from '../../shared/components';
import { useAccount } from '../hooks/useAccount';
import { useAccountSubscription } from '../hooks/useAccountSubscription';

import { AccountContextProvider } from './AccountContextProvider';
import { AccountHeading } from './AccountHeading';
import { SDKUsageErrorBoundary } from './SDKUsageErrorBoundary';

export function SDKApplicationDetailPage() {
  const { accountId, applicationId } = useParams<{ accountId: string; applicationId: string }>();
  const navigate = useNavigate();
  const accountQuery = useAccount(accountId ?? '');
  const account = accountId ? accountQuery.data?.entities?.accounts?.[accountId] : undefined;
  const { data: subscription } = useAccountSubscription(account || null);
  const handleBackClick = () => {
    if (accountId) {
      navigate(`/accounts/${accountId}/sdks`);
    }
  };

  if (!accountId || !applicationId) {
    return (
      <ErrorCallout>
        <strong>Invalid URL</strong>
        <p>Missing account ID or application ID in the URL.</p>
      </ErrorCallout>
    );
  }

  if (accountQuery.isLoading) {
    return (
      <div className="SDKApplicationDetailPage">
        <Flex>
          <Box width={1} p={2}>
            <Card>Loading account data…</Card>
          </Box>
        </Flex>
      </div>
    );
  }

  if (accountQuery.error || !account) {
    return (
      <div className="SDKApplicationDetailPage">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <ErrorCallout>
                <strong>Failed to load account data</strong>
                <p>Account not found or could not be loaded.</p>
              </ErrorCallout>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  return (
    <div className="SDKApplicationDetailPage">
      <AccountHeading account={account} />
      <Flex>
        <Box width={1} p={2}>
          <Card>
            <SDKUsageErrorBoundary>
              <Suspense fallback={<div>Loading SDK connection details…</div>}>
                <AccountContextProvider account={account} subscription={subscription || undefined}>
                  <SDKApplicationDetail applicationId={applicationId} onBackClick={handleBackClick} />
                </AccountContextProvider>
              </Suspense>
            </SDKUsageErrorBoundary>
          </Card>
        </Box>
      </Flex>
    </div>
  );
}
